import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Divider, FormControlLabel,
  MenuItem, Switch, TextField, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { DEFAULT_CHECKLIST_QUESTIONS, DEFAULT_CHECKLIST_MAX_QUESTIONS,
  MAX_CHECKLIST_QUESTIONS, toChecklistQuestionRows } from '../utils/checklistConfiguration';

const PLAN_LIMITS = {
  Mini: [3, 100], Starter: [3, 1000], Professional: [5, 3000], Business: [15, 10000],
};
export const createRegisterSetup = (planType = 'Mini') => ({
  planType, userLimit: '', itemLimit: '', rfidStatisticsEnabled: false,
  checklistEnabled: false, supervisorEditEnabled: false, useDefaultQuestions: true,
  maxQuestions: DEFAULT_CHECKLIST_MAX_QUESTIONS, questions: [...DEFAULT_CHECKLIST_QUESTIONS],
});
const questionsFor = value => value.questions.map(question => question.trim().replace(/\s+/g, ' ')).filter(Boolean);
export const registerSetupError = value => {
  if (value.planType === 'Custom' && [value.userLimit, value.itemLimit].some(limit =>
    !/^\d+$/.test(String(limit)) || Number(limit) > 2147483647)) {
    return 'Enter both custom limits as whole numbers. Use 0 for unlimited.';
  }
  const questions = questionsFor(value);
  if (questions.length > value.maxQuestions) return `There are ${questions.length} filled questions. Clear extra questions or increase the approved limit of ${value.maxQuestions}.`;
  if (new Set(questions).size !== questions.length) return 'Each inspection question must be unique.';
  if (value.checklistEnabled && !questions.length && !value.useDefaultQuestions) return 'Enter at least one inspection question or use the default questions.';
  return '';
};
export const registerSetupPayload = value => ({
  planType: value.planType,
  ...(value.planType === 'Custom' ? { userLimit: Number(value.userLimit), itemLimit: Number(value.itemLimit) } : {}),
  rfidStatisticsEnabled: value.rfidStatisticsEnabled, checklistEnabled: value.checklistEnabled,
  supervisorEditEnabled: value.supervisorEditEnabled, useDefaultQuestions: value.useDefaultQuestions,
  maxQuestions: value.maxQuestions, questions: questionsFor(value),
});

export default function RegisterSetupFields({ value, onChange, disabled = false }) {
  const change = patch => onChange(previous => ({ ...previous, ...patch }));
  const error = registerSetupError(value);
  const limits = PLAN_LIMITS[value.planType];
  return <Box component="fieldset" disabled={disabled} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
    <Divider sx={{ my: 3 }} />
    <Typography variant="subtitle1" fontWeight={600}>Plan and limits</Typography>
    <TextField select label="Plan Type" value={value.planType} fullWidth sx={{ mt: 2 }} disabled={disabled}
      onChange={event => change({ planType: event.target.value })}>
      {[...Object.keys(PLAN_LIMITS), 'Custom'].map(plan => <MenuItem key={plan} value={plan}>{plan}</MenuItem>)}
    </TextField>
    {limits && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
      Includes {limits[0]} users and {limits[1].toLocaleString('en-GB')} equipment items. Choose Custom to set different limits.
    </Typography>}
    {value.planType === 'Custom' && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
      <TextField label="Custom User Limit" type="number" value={value.userLimit} required fullWidth disabled={disabled}
        inputProps={{ min: 0, max: 2147483647, step: 1 }} helperText="0 means unlimited users."
        onChange={event => change({ userLimit: event.target.value })} />
      <TextField label="Custom Item Limit" type="number" value={value.itemLimit} required fullWidth disabled={disabled}
        inputProps={{ min: 0, max: 2147483647, step: 1 }} helperText="0 means unlimited equipment items."
        onChange={event => change({ itemLimit: event.target.value })} />
    </Box>}
    <Divider sx={{ my: 3 }} />
    <Typography variant="subtitle1" fontWeight={600}>Register options</Typography>
    <FormControlLabel control={<Switch checked={value.rfidStatisticsEnabled} disabled={disabled}
      onChange={event => change({ rfidStatisticsEnabled: event.target.checked })} />}
      label="Show RFID statistics on this register's dashboard" />
    <Typography variant="body2" color="text.secondary">Tag scanning remains available when statistics are hidden.</Typography>
    <Box sx={{ mt: 2 }}>
      <FormControlLabel control={<Switch checked={value.checklistEnabled} disabled={disabled}
        onChange={event => change({ checklistEnabled: event.target.checked })} />}
        label="Enable inspection checklist" />
    </Box>
    {value.checklistEnabled && <>
      <FormControlLabel control={<Switch checked={value.supervisorEditEnabled} disabled={disabled}
        onChange={event => change({ supervisorEditEnabled: event.target.checked })} />}
        label="Allow register supervisors to edit inspection questions" />
      <TextField select label="Approved question limit" value={value.maxQuestions} fullWidth sx={{ mt: 2 }}
        disabled={disabled} SelectProps={{ native: true }}
        helperText="Inspectors see only the questions that are filled in. Default prefill contains 20 questions."
        onChange={event => { const maxQuestions = Number(event.target.value); change({ maxQuestions, questions: toChecklistQuestionRows(value.questions, maxQuestions) }); }}>
        {Array.from({ length: MAX_CHECKLIST_QUESTIONS - DEFAULT_CHECKLIST_MAX_QUESTIONS + 1 }, (_, index) => {
          const limit = DEFAULT_CHECKLIST_MAX_QUESTIONS + index;
          return <option key={limit} value={limit}>{limit}</option>;
        })}
      </TextField>
    </>}
    <Accordion disableGutters elevation={0} sx={{ mt: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="body2">Inspection questions · {questionsFor(value).length} configured{!value.checklistEnabled ? ' (checklist off)' : ''}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <FormControlLabel control={<Switch checked={value.useDefaultQuestions} disabled={disabled}
          onChange={event => change({ useDefaultQuestions: event.target.checked,
            ...(event.target.checked ? { questions: toChecklistQuestionRows(DEFAULT_CHECKLIST_QUESTIONS, value.maxQuestions) } : {}) })} />}
          label="Use default checklist questions (prefill)" />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Edit questions now or keep the defaults. Leave unused rows blank.</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
          {toChecklistQuestionRows(value.questions, value.maxQuestions).map((question, index) => <TextField
            key={index} label={`Question ${index + 1}`} value={question} size="small" fullWidth disabled={disabled}
            inputProps={{ maxLength: 500 }} onChange={event => {
              const questions = toChecklistQuestionRows(value.questions, value.maxQuestions);
              questions[index] = event.target.value;
              change({ questions, useDefaultQuestions: false });
            }} />)}
        </Box>
      </AccordionDetails>
    </Accordion>
    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
  </Box>;
}
